import React, { useState, useEffect } from 'react';
import { X, Shield, TrendingDown, TrendingUp, AlertTriangle, Building2, Bitcoin, FileText, Download, ExternalLink, Zap, Target, DollarSign, Activity, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { jsPDF } from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════
// SCORING ALGORITHM - Based on AssumeRate Demo Document
// ═══════════════════════════════════════════════════════════════════

const scoreDeRiskOptions = (property) => {
  const dscr = property.dscr || 0.75;
  const ltv = property.ltv_ratio || property.currentLTV || 80;
  const status = property.status || 'Watchlist';
  const credIQ = property.credIQScore || property.cred_iq_score || 'C';
  
  let btc = 0, note = 0, dip = 0;
  
  // ═══════════════════════════════════════════════════════════════════
  // DSCR SCORING (35 points max)
  // ═══════════════════════════════════════════════════════════════════
  if (dscr >= 0.85) {
    btc += 35; note += 18; dip += 5;
  } else if (dscr >= 0.70) {
    btc += 25; note += 28; dip += 15;
  } else if (dscr >= 0.50) {
    btc += 10; note += 35; dip += 28;
  } else {
    btc += 0; note += 30; dip += 35;
  }
  
  // ═══════════════════════════════════════════════════════════════════
  // LTV SCORING (30 points max)
  // ═══════════════════════════════════════════════════════════════════
  if (ltv <= 75) {
    btc += 30; note += 10; dip += 5;
  } else if (ltv <= 85) {
    btc += 22; note += 20; dip += 12;
  } else if (ltv <= 95) {
    btc += 10; note += 28; dip += 22;
  } else {
    btc += 0; note += 25; dip += 30;
  }
  
  // ═══════════════════════════════════════════════════════════════════
  // LOAN STATUS SCORING (20 points max)
  // ═══════════════════════════════════════════════════════════════════
  const statusLower = status.toLowerCase();
  if (statusLower.includes('90') || statusLower.includes('late')) {
    btc += 4; note += 20; dip += 16;
  } else if (statusLower.includes('maturity') || statusLower.includes('default')) {
    btc += 0; note += 14; dip += 20;
  } else if (statusLower.includes('special')) {
    btc += 8; note += 16; dip += 18;
  } else if (statusLower.includes('watchlist') || statusLower.includes('dscr')) {
    btc += 20; note += 12; dip += 6;
  } else {
    btc += 15; note += 10; dip += 5;
  }
  
  // ═══════════════════════════════════════════════════════════════════
  // CRED-IQ RATING SCORING (15 points max)
  // ═══════════════════════════════════════════════════════════════════
  const grade = credIQ.toUpperCase();
  if (grade.includes('C+') || grade.includes('B')) {
    btc += 15; note += 5; dip += 2;
  } else if (grade === 'C' || grade.includes('C-')) {
    btc += 10; note += 10; dip += 8;
  } else if (grade.includes('D+')) {
    btc += 5; note += 12; dip += 12;
  } else if (grade === 'D') {
    btc += 2; note += 14; dip += 14;
  } else {
    btc += 0; note += 12; dip += 15;
  }
  
  // ═══════════════════════════════════════════════════════════════════
  // RANK PATHWAYS & CALCULATE CONFIDENCE
  // ═══════════════════════════════════════════════════════════════════
  const scores = [
    { id: 'btcBridge', name: 'BTC Bridge Loan', score: btc },
    { id: 'noteSale', name: 'Note Sale', score: note },
    { id: 'dip', name: 'DIP Financing', score: dip }
  ].sort((a, b) => b.score - a.score);
  
  const gap = scores[0].score - scores[1].score;
  const conf = Math.min(97, Math.round(
    gap >= 25 ? 85 + Math.min(10, gap - 25) :
    gap >= 12 ? 68 + gap :
    52 + gap
  ));
  
  const confLabel = conf >= 82 ? 'HIGH' : conf >= 60 ? 'MEDIUM' : 'LOW';
  const confColor = conf >= 82 ? 'green' : conf >= 60 ? 'yellow' : 'red';
  
  return {
    scores,
    confidence: conf,
    confidenceLabel: confLabel,
    confidenceColor: confColor,
    gap,
    rawScores: { btc, note, dip }
  };
};

// ═══════════════════════════════════════════════════════════════════
// SIGNAL HELPERS
// ═══════════════════════════════════════════════════════════════════

const dscrSignal = (dscr) => {
  if (dscr >= 1.25) return { pct: 100, color: 'bg-green-500', label: 'Healthy', textColor: 'text-green-600' };
  if (dscr >= 1.0) return { pct: 75, color: 'bg-green-400', label: 'Adequate', textColor: 'text-green-500' };
  if (dscr >= 0.8) return { pct: 50, color: 'bg-yellow-500', label: 'Stressed', textColor: 'text-yellow-600' };
  if (dscr >= 0.6) return { pct: 35, color: 'bg-orange-500', label: 'Impaired', textColor: 'text-orange-600' };
  return { pct: 20, color: 'bg-red-500', label: 'Critical', textColor: 'text-red-600' };
};

const ltvSignal = (ltv) => {
  if (ltv <= 65) return { pct: 100, color: 'bg-green-500', label: 'Low Risk', textColor: 'text-green-600' };
  if (ltv <= 75) return { pct: 75, color: 'bg-green-400', label: 'Moderate', textColor: 'text-green-500' };
  if (ltv <= 85) return { pct: 50, color: 'bg-yellow-500', label: 'Elevated', textColor: 'text-yellow-600' };
  if (ltv <= 95) return { pct: 35, color: 'bg-orange-500', label: 'High', textColor: 'text-orange-600' };
  return { pct: 20, color: 'bg-red-500', label: 'Critical', textColor: 'text-red-600' };
};

const statusSignal = (status) => {
  const s = status.toLowerCase();
  if (s.includes('maturity') || s.includes('default')) return { color: 'bg-red-100 text-red-700 border-red-200', severity: 'Critical' };
  if (s.includes('90') || s.includes('late')) return { color: 'bg-red-100 text-red-700 border-red-200', severity: 'Severe' };
  if (s.includes('special')) return { color: 'bg-orange-100 text-orange-700 border-orange-200', severity: 'High' };
  if (s.includes('watchlist') || s.includes('dscr')) return { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', severity: 'Elevated' };
  return { color: 'bg-gray-100 text-gray-700 border-gray-200', severity: 'Normal' };
};

const credIQSignal = (rating) => {
  const r = rating.toUpperCase();
  if (r.includes('D-')) return { color: 'text-red-600', bgColor: 'bg-red-100', label: 'D- (Critical)' };
  if (r === 'D') return { color: 'text-red-500', bgColor: 'bg-red-50', label: 'D (High Risk)' };
  if (r.includes('D+')) return { color: 'text-orange-600', bgColor: 'bg-orange-100', label: 'D+ (Elevated)' };
  if (r.includes('C-')) return { color: 'text-orange-500', bgColor: 'bg-orange-50', label: 'C- (Watch)' };
  if (r === 'C') return { color: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'C (Moderate)' };
  if (r.includes('C+')) return { color: 'text-green-600', bgColor: 'bg-green-100', label: 'C+ (Stable)' };
  return { color: 'text-green-700', bgColor: 'bg-green-100', label: 'B+ (Strong)' };
};

// ═══════════════════════════════════════════════════════════════════
// PATHWAY DETAILS
// ═══════════════════════════════════════════════════════════════════

const pathwayDetails = {
  btcBridge: {
    icon: Bitcoin,
    title: 'BTC Bridge Loan',
    description: 'Short-term bridge financing with 150% Bitcoin overcollateralization. Provides double collateral coverage: distressed RE asset + liquid BTC collateral.',
    rateLabel: 'Rate',
    rateCalc: (p) => `${(p.btcLendingRate || 8.5).toFixed(1)}% APR`,
    pros: ['Double collateral protection', 'Fast execution (7-14 days)', 'Non-recourse structure', 'No personal guarantees'],
    cons: ['Requires BTC collateral', 'Higher rate than traditional', 'Margin call risk on BTC'],
    suitability: 'Best for borrowers with BTC holdings seeking to preserve RE position'
  },
  noteSale: {
    icon: DollarSign,
    title: 'Note Sale',
    description: 'Outright sale of distressed note to qualified investor. Clean exit from exposure with immediate liquidity.',
    rateLabel: 'Recovery',
    rateCalc: (p) => `${Math.round(100 - (p.discount || 25))}¢ on $1`,
    pros: ['Immediate liquidity', 'Clean exit', 'No ongoing exposure', 'Simplified accounting'],
    cons: ['Below-par recovery', 'Loss recognition', 'Market timing risk'],
    suitability: 'Best for lenders seeking to exit distressed exposure quickly'
  },
  dip: {
    icon: AlertTriangle,
    title: 'DIP Financing',
    description: 'Debtor-in-Possession financing for late-stage defaults. Priority lien status with BTC overcollateralization for downside protection.',
    rateLabel: 'Rate',
    rateCalc: (p) => 'Prime + 450bps',
    pros: ['Priority lien status', 'Court-supervised', 'BTC protection', 'Restructuring opportunity'],
    cons: ['Complex legal process', 'Extended timeline', 'Requires bankruptcy filing'],
    suitability: 'Best for assets in or approaching bankruptcy with restructuring potential'
  }
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

const DeRiskAnalysisPanel = ({ property, isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [narrativeLoading, setNarrativeLoading] = useState(true);
  const [narrative, setNarrative] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [btcPrice, setBtcPrice] = useState(70000);
  const [galoyStatus, setGaloyStatus] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Format helpers
  const fmt = (n) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${(n / 1e3).toFixed(0)}K`;
  const fmtBtc = (usd) => `₿${(usd / btcPrice).toFixed(2)}`;
  
  // PDF Export Function
  const generatePDF = async () => {
    if (!propertyData || !aiResult) return;
    
    setExportingPdf(true);
    
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;
      
      // Colors
      const primaryBlue = [0, 106, 255];
      const darkText = [42, 42, 51];
      const grayText = [107, 107, 118];
      const greenColor = [34, 197, 94];
      const redColor = [239, 68, 68];
      const orangeColor = [249, 115, 22];
      
      // Header with gradient effect
      doc.setFillColor(...primaryBlue);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      // Logo/Brand
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('AssumeRate', margin, 20);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('AI De-Risk Analysis Memo', margin, 28);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, 35);
      
      // Confidence badge
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(pageWidth - 55, 12, 40, 22, 3, 3, 'F');
      doc.setTextColor(...primaryBlue);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(`${aiResult.confidence}%`, pageWidth - 35, 25, { align: 'center' });
      doc.setFontSize(7);
      doc.text('CONFIDENCE', pageWidth - 35, 31, { align: 'center' });
      
      yPos = 55;
      
      // Property Overview Section
      doc.setTextColor(...darkText);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('PROPERTY OVERVIEW', margin, yPos);
      yPos += 8;
      
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
      
      // Property details
      doc.setFontSize(16);
      doc.text(propertyData.name, margin, yPos);
      yPos += 6;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayText);
      doc.text(`${propertyData.address}${propertyData.city ? `, ${propertyData.city}` : ''}${propertyData.state ? `, ${propertyData.state}` : ''}`, margin, yPos);
      yPos += 5;
      doc.text(`ID: ${propertyData.id} | Type: ${propertyData.type}`, margin, yPos);
      yPos += 10;
      
      // Key Metrics Grid
      const metrics = [
        { label: 'DSCR', value: `${propertyData.dscr.toFixed(2)}x`, color: propertyData.dscr >= 1.0 ? greenColor : redColor },
        { label: 'LTV', value: `${propertyData.ltv}%`, color: propertyData.ltv <= 75 ? greenColor : propertyData.ltv <= 85 ? orangeColor : redColor },
        { label: 'Loan Balance', value: fmt(propertyData.loanBalance), color: darkText },
        { label: 'Property Value', value: fmt(propertyData.propertyValue), color: darkText },
        { label: 'Occupancy', value: `${propertyData.occupancy}%`, color: propertyData.occupancy >= 70 ? greenColor : orangeColor },
        { label: 'Cred-IQ', value: propertyData.credIQScore, color: primaryBlue },
      ];
      
      const colWidth = (pageWidth - 2 * margin) / 3;
      metrics.forEach((metric, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const xPos = margin + col * colWidth;
        const boxY = yPos + row * 22;
        
        doc.setFillColor(247, 247, 247);
        doc.roundedRect(xPos, boxY, colWidth - 5, 18, 2, 2, 'F');
        
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text(metric.label, xPos + 5, boxY + 6);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...metric.color);
        doc.text(metric.value, xPos + 5, boxY + 14);
        doc.setFont('helvetica', 'normal');
      });
      
      yPos += 50;
      
      // AI Analysis Section
      doc.setTextColor(...darkText);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('AI DE-RISK PATHWAYS', margin, yPos);
      yPos += 8;
      
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;
      
      // Ranked pathways
      const sortedPathways = [...aiResult.scores].sort((a, b) => b.score - a.score);
      sortedPathways.forEach((pathway, index) => {
        const pathwayDesc = pathwayDetails[pathway.id]?.description || '';
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }
        
        const isRecommended = index === 0;
        
        // Pathway box
        if (isRecommended) {
          doc.setFillColor(232, 244, 253);
          doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');
        }
        
        // Rank badge
        doc.setFillColor(...(isRecommended ? primaryBlue : grayText));
        doc.circle(margin + 8, yPos + 10, 6, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}`, margin + 8, yPos + 13, { align: 'center' });
        
        // Pathway name and score
        doc.setTextColor(...darkText);
        doc.setFontSize(11);
        doc.text(pathway.name, margin + 18, yPos + 8);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...grayText);
        
        // Score and details
        const scoreText = `Score: ${pathway.score}/100`;
        const detailText = pathway.name.includes('BTC') 
          ? `${propertyData.btcLendingRate}% APR | ${fmtBtc(propertyData.loanBalance)} equivalent`
          : pathway.name.includes('Note') 
            ? `Est. ${100 - propertyData.discount}¢ on $1`
            : pathwayDesc.substring(0, 50) + '...' || '';
        
        doc.text(`${scoreText} | ${detailText}`, margin + 18, yPos + 16);
        
        if (isRecommended) {
          doc.setTextColor(...greenColor);
          doc.setFontSize(8);
          doc.text('RECOMMENDED', pageWidth - margin - 25, yPos + 8);
        }
        
        yPos += isRecommended ? 40 : 28;
      });
      
      // AI Narrative Section
      if (narrative) {
        yPos += 5;
        if (yPos > pageHeight - 80) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.setTextColor(...darkText);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('AI ANALYSIS NARRATIVE', margin, yPos);
        yPos += 8;
        
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...grayText);
        
        // Split narrative into lines
        const splitText = doc.splitTextToSize(narrative, pageWidth - 2 * margin);
        splitText.forEach(line => {
          if (yPos > pageHeight - 30) {
            doc.addPage();
            yPos = margin;
          }
          doc.text(line, margin, yPos);
          yPos += 5;
        });
      }
      
      // Footer on each page
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text(`AssumeRate De-Risk Analysis | Confidential`, margin, pageHeight - 10);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        
        // Disclaimer
        if (i === totalPages) {
          doc.setFontSize(7);
          doc.text('This analysis is for informational purposes only and does not constitute financial advice.', margin, pageHeight - 5);
        }
      }
      
      // Save the PDF
      const fileName = `AssumeRate_DeRisk_${propertyData.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };
  
  // Normalize property data from different sources
  const normalizeProperty = (p) => {
    if (!p) return null;
    return {
      id: p.id || p.loanId || 'CRE-0000',
      name: p.name || 'Unknown Property',
      type: p.property_type || p.type || 'Commercial',
      address: p.address || p.full_address || 'Address not available',
      city: p.city || '',
      state: p.state || '',
      dscr: parseFloat(p.dscr) || 0.75,
      ltv: parseFloat(p.ltv_ratio || p.currentLTV) || 80,
      status: p.status || 'Watchlist',
      credIQScore: p.credIQScore || p.cred_iq_score || 'C',
      loanBalance: parseFloat(p.loan_amount || p.loanBalance) || 10000000,
      propertyValue: parseFloat(p.property_value || p.appraisedValue) || 15000000,
      occupancy: parseFloat(p.occupancy_rate || p.occupancy) || 65,
      noi: parseFloat(p.noi) || 500000,
      btcLendingRate: parseFloat(p.btcLendingRate || p.interest_rate) || 8.5,
      discount: parseFloat(p.discount) || 25,
      maturityDate: p.maturityDate || p.maturity_date || '2025-12-31',
      capRate: parseFloat(p.cap_rate) || 6.5,
      sqft: parseFloat(p.size_sqft || p.sqft) || 100000
    };
  };

  const propertyData = normalizeProperty(property);

  useEffect(() => {
    if (isOpen && propertyData) {
      setIsLoading(true);
      setNarrativeLoading(true);
      
      // Fetch BTC price
      fetchBtcPrice();
      
      // Check Galoy status
      checkGaloyStatus();
      
      // Calculate AI scores
      setTimeout(() => {
        const result = scoreDeRiskOptions(propertyData);
        setAiResult(result);
        setIsLoading(false);
        
        // Generate narrative
        generateNarrative(result);
      }, 800);
    }
  }, [isOpen, property]);

  const fetchBtcPrice = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crypto/prices`);
      const data = await response.json();
      if (data.success) {
        setBtcPrice(data.prices.BTC.price);
      }
    } catch (error) {
      console.error('Error fetching BTC price:', error);
    }
  };

  const checkGaloyStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/galoy/status`);
      const data = await response.json();
      setGaloyStatus(data);
    } catch (error) {
      console.error('Error checking Galoy status:', error);
      setGaloyStatus({ connected: false });
    }
  };

  const generateNarrative = async (result) => {
    setNarrativeLoading(true);
    
    const topPathway = result.scores[0];
    const secondPathway = result.scores[1];
    
    // Build contextual narrative based on scoring
    let narrative = '';
    
    const dscrDesc = propertyData.dscr < 0.7 ? 'severely impaired' : propertyData.dscr < 0.9 ? 'stressed' : 'weakened';
    const ltvDesc = propertyData.ltv > 90 ? 'critically elevated' : propertyData.ltv > 80 ? 'elevated' : 'moderate';
    
    if (topPathway.id === 'noteSale') {
      narrative = `CREDIT COMMITTEE RECOMMENDATION: Based on comprehensive analysis of ${propertyData.name}, we recommend pursuing a NOTE SALE strategy with ${result.confidence}% confidence. The property exhibits ${dscrDesc} debt service coverage (DSCR: ${propertyData.dscr.toFixed(2)}x) combined with ${ltvDesc} LTV (${propertyData.ltv}%), indicating fundamental impairment that favors a clean exit over restructuring. Current market conditions and the ${propertyData.status} designation suggest achievable recovery of approximately ${Math.round(100 - propertyData.discount)}¢ on the dollar through AssumeRate's qualified buyer network. Secondary consideration: ${secondPathway.name} (Score: ${secondPathway.score}/100).`;
    } else if (topPathway.id === 'btcBridge') {
      const btcRequired = (propertyData.loanBalance * 1.5 / btcPrice).toFixed(2);
      narrative = `CREDIT COMMITTEE RECOMMENDATION: Analysis of ${propertyData.name} indicates strong suitability for a BTC BRIDGE LOAN structure with ${result.confidence}% confidence. Despite ${dscrDesc} debt service (DSCR: ${propertyData.dscr.toFixed(2)}x), the underlying asset fundamentals remain recoverable with capital injection. The 150% Bitcoin overcollateralization (≈${btcRequired} BTC at current prices) provides double collateral coverage—both the real estate at current distressed value AND liquid BTC collateral. Recommended bridge rate: ${propertyData.btcLendingRate}% APR reflects the risk-adjusted return appropriate for this credit profile. Secondary consideration: ${secondPathway.name} (Score: ${secondPathway.score}/100).`;
    } else {
      narrative = `CREDIT COMMITTEE RECOMMENDATION: ${propertyData.name} presents characteristics consistent with late-stage distress requiring DIP FINANCING consideration with ${result.confidence}% confidence. The combination of ${dscrDesc} coverage (DSCR: ${propertyData.dscr.toFixed(2)}x), ${ltvDesc} LTV (${propertyData.ltv}%), and ${propertyData.status} status suggests conventional restructuring pathways are likely exhausted. DIP financing with BTC overcollateralization can provide necessary liquidity while protecting against further asset deterioration during workout proceedings. Court supervision provides structured resolution path. Secondary consideration: ${secondPathway.name} (Score: ${secondPathway.score}/100).`;
    }
    
    setTimeout(() => {
      setNarrative(narrative);
      setNarrativeLoading(false);
    }, 1200);
  };

  const formatCurrency = (amount) => {
    if (amount >= 1e9) return `$${(amount / 1e9).toFixed(2)}B`;
    if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
    if (amount >= 1e3) return `$${(amount / 1e3).toFixed(0)}K`;
    return `$${amount}`;
  };

  const exportMemo = () => {
    if (!propertyData || !aiResult) return;
    
    const topPathway = aiResult.scores[0];
    const memo = `
═══════════════════════════════════════════════════════════════════
AI DE-RISK ANALYSIS MEMO
AssumeRate Intelligence Engine
Generated: ${new Date().toISOString()}
═══════════════════════════════════════════════════════════════════

PROPERTY IDENTIFICATION
───────────────────────────────────────────────────────────────────
Loan ID: ${propertyData.id}
Property: ${propertyData.name}
Type: ${propertyData.type}
Address: ${propertyData.address}
City/State: ${propertyData.city}, ${propertyData.state}

FINANCIAL METRICS
───────────────────────────────────────────────────────────────────
Property Value: ${formatCurrency(propertyData.propertyValue)}
Loan Balance: ${formatCurrency(propertyData.loanBalance)}
Current LTV: ${propertyData.ltv}%
DSCR: ${propertyData.dscr.toFixed(2)}x
NOI: ${formatCurrency(propertyData.noi)}
Cap Rate: ${propertyData.capRate}%
Occupancy: ${propertyData.occupancy}%
Maturity Date: ${propertyData.maturityDate}

RISK ASSESSMENT
───────────────────────────────────────────────────────────────────
Loan Status: ${propertyData.status}
Cred-IQ Rating: ${propertyData.credIQScore}
AI Confidence: ${aiResult.confidence}% (${aiResult.confidenceLabel})

RANKED DE-RISK PATHWAYS
───────────────────────────────────────────────────────────────────
#1 RECOMMENDED: ${aiResult.scores[0].name} (Score: ${aiResult.scores[0].score}/100)
#2 ALTERNATIVE: ${aiResult.scores[1].name} (Score: ${aiResult.scores[1].score}/100)
#3 NOT ADVISED: ${aiResult.scores[2].name} (Score: ${aiResult.scores[2].score}/100)

Score Gap (Confidence Driver): ${aiResult.gap} points

AI ANALYST NARRATIVE
───────────────────────────────────────────────────────────────────
${narrative}

═══════════════════════════════════════════════════════════════════
DISCLAIMER: This analysis is generated by AI and should be reviewed
by qualified professionals before making investment decisions.
═══════════════════════════════════════════════════════════════════
`;
    
    const blob = new Blob([memo], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `derisk-memo-${propertyData.id}-${Date.now()}.txt`;
    a.click();
  };

  if (!isOpen) return null;

  const dscrSig = dscrSignal(propertyData?.dscr || 0.75);
  const ltvSig = ltvSignal(propertyData?.ltv || 80);
  const statusSig = statusSignal(propertyData?.status || 'Watchlist');
  const credIQSig = credIQSignal(propertyData?.credIQScore || 'C');

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#006AFF] to-[#0055CC] p-5 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">AI De-Risk Analysis</h2>
                <p className="text-sm text-blue-100">AssumeRate Intelligence Engine v2.0</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          
          {/* Galoy Status Indicator */}
          <div className="mt-3 flex items-center gap-2 text-xs text-blue-100">
            <div className={`w-2 h-2 rounded-full ${galoyStatus?.connected ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`} />
            <span>Galoy Banking: {galoyStatus?.connected ? 'Connected' : 'Simulation Mode'}</span>
            <span className="mx-2">|</span>
            <span>BTC: ${btcPrice.toLocaleString()}</span>
          </div>
        </div>

        {isLoading ? (
          <div className="p-12 flex flex-col items-center justify-center min-h-[500px]">
            <div className="relative">
              <Activity className="h-16 w-16 text-[#006AFF] animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Shield className="h-6 w-6 text-[#006AFF]" />
              </div>
            </div>
            <p className="text-[#6B6B76] mt-4 text-lg">Analyzing risk signals...</p>
            <p className="text-[#9B9BA5] text-sm mt-1">Calculating de-risk pathways</p>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Property Header */}
            <div className="bg-gradient-to-br from-[#F7F7F7] to-white rounded-xl p-5 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-[#006AFF] font-semibold">{propertyData.id}</span>
                    <span className="text-[#9B9BA5]">•</span>
                    <span className="text-sm text-[#6B6B76]">{propertyData.type.toUpperCase()}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#2A2A33] mb-1">{propertyData.name}</h3>
                  <p className="text-sm text-[#6B6B76]">{propertyData.address}</p>
                  {propertyData.city && (
                    <p className="text-sm text-[#9B9BA5]">{propertyData.city}, {propertyData.state}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#6B6B76] mb-1">Loan Balance</div>
                  <div className="text-2xl font-bold text-[#2A2A33]">{formatCurrency(propertyData.loanBalance)}</div>
                  <div className="text-xs text-[#6B6B76] mt-1">Maturity: {propertyData.maturityDate}</div>
                </div>
              </div>
            </div>

            {/* AI Confidence Score */}
            {aiResult && (
              <div className={`rounded-xl p-5 border-2 ${
                aiResult.confidenceColor === 'green' ? 'border-green-300 bg-green-50' :
                aiResult.confidenceColor === 'yellow' ? 'border-yellow-300 bg-yellow-50' :
                'border-red-300 bg-red-50'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`text-5xl font-bold ${
                      aiResult.confidenceColor === 'green' ? 'text-green-600' :
                      aiResult.confidenceColor === 'yellow' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {aiResult.confidence}%
                    </div>
                    <div>
                      <div className={`text-lg font-bold ${
                        aiResult.confidenceColor === 'green' ? 'text-green-700' :
                        aiResult.confidenceColor === 'yellow' ? 'text-yellow-700' :
                        'text-red-700'
                      }`}>
                        {aiResult.confidenceLabel} CONFIDENCE
                      </div>
                      <div className="text-sm text-[#6B6B76]">
                        Signal agreement across {aiResult.gap >= 20 ? 'strong' : aiResult.gap >= 10 ? 'moderate' : 'weak'} indicators
                      </div>
                    </div>
                  </div>
                  <div className="text-5xl">
                    {aiResult.confidenceColor === 'green' ? '✅' : 
                     aiResult.confidenceColor === 'yellow' ? '⚠️' : '🔴'}
                  </div>
                </div>
              </div>
            )}

            {/* Risk Signal Breakdown */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="text-sm font-bold text-[#2A2A33] mb-4 flex items-center">
                <Activity className="h-4 w-4 mr-2 text-[#006AFF]" />
                Risk Signal Breakdown
              </h4>
              
              <div className="space-y-4">
                {/* DSCR */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-[#6B6B76]">DSCR (Debt Service Coverage Ratio)</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${dscrSig.color} text-white`}>
                        {dscrSig.label}
                      </span>
                      <span className={`text-sm font-bold ${dscrSig.textColor}`}>
                        {propertyData.dscr.toFixed(2)}x
                      </span>
                    </div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${dscrSig.color} transition-all duration-700`}
                      style={{ width: `${dscrSig.pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-[#9B9BA5] mt-1">
                    <span>0.0x</span>
                    <span className="text-red-500 font-medium">← 1.0x threshold</span>
                    <span>2.0x+</span>
                  </div>
                </div>

                {/* LTV */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-[#6B6B76]">Current LTV (Loan-to-Value)</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${ltvSig.color} text-white`}>
                        {ltvSig.label}
                      </span>
                      <span className={`text-sm font-bold ${ltvSig.textColor}`}>
                        {propertyData.ltv}%
                      </span>
                    </div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${ltvSig.color} transition-all duration-700`}
                      style={{ width: `${Math.min(100, propertyData.ltv)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-[#9B9BA5] mt-1">
                    <span>0%</span>
                    <span className="text-orange-500 font-medium">← 80% threshold</span>
                    <span>100%+</span>
                  </div>
                </div>

                {/* Status & Cred-IQ */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className={`rounded-lg p-4 border ${statusSig.color}`}>
                    <div className="text-xs text-[#6B6B76] mb-1">Loan Status</div>
                    <div className="font-bold">{propertyData.status}</div>
                    <div className="text-xs mt-1 opacity-80">Severity: {statusSig.severity}</div>
                  </div>
                  <div className={`rounded-lg p-4 ${credIQSig.bgColor}`}>
                    <div className="text-xs text-[#6B6B76] mb-1">Cred-IQ Rating</div>
                    <div className={`font-bold ${credIQSig.color}`}>{propertyData.credIQScore}</div>
                    <div className={`text-xs mt-1 ${credIQSig.color}`}>{credIQSig.label}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ranked De-Risk Pathways */}
            {aiResult && (
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-[#2A2A33] flex items-center">
                  <Target className="h-4 w-4 mr-2 text-[#006AFF]" />
                  Ranked De-Risk Pathways
                </h4>
                
                {aiResult.scores.map((pathway, index) => {
                  const isRecommended = index === 0;
                  const isNotAdvised = index === 2;
                  const details = pathwayDetails[pathway.id];
                  const PathwayIcon = details.icon;
                  
                  return (
                    <div 
                      key={pathway.id}
                      className={`rounded-xl overflow-hidden border-2 transition-all ${
                        isRecommended 
                          ? 'border-[#006AFF] bg-gradient-to-br from-[#E8F4FD] to-white shadow-lg' 
                          : isNotAdvised 
                            ? 'border-gray-200 bg-gray-50 opacity-60'
                            : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2.5 rounded-xl ${
                              isRecommended ? 'bg-[#006AFF] shadow-lg' : isNotAdvised ? 'bg-gray-300' : 'bg-gray-100'
                            }`}>
                              <PathwayIcon className={`h-5 w-5 ${isRecommended ? 'text-white' : 'text-[#6B6B76]'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                  isRecommended ? 'bg-[#006AFF] text-white' : 
                                  isNotAdvised ? 'bg-gray-400 text-white' : 
                                  'bg-gray-200 text-[#6B6B76]'
                                }`}>
                                  #{index + 1} {isRecommended ? 'RECOMMENDED' : isNotAdvised ? 'NOT ADVISED' : 'ALTERNATIVE'}
                                </span>
                              </div>
                              <div className={`text-lg font-bold ${isRecommended ? 'text-[#006AFF]' : 'text-[#2A2A33]'}`}>
                                {details.title}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-3xl font-bold ${
                              isRecommended ? 'text-[#006AFF]' : isNotAdvised ? 'text-gray-400' : 'text-[#2A2A33]'
                            }`}>
                              {pathway.score}
                            </div>
                            <div className="text-xs text-[#6B6B76]">/ 100 pts</div>
                          </div>
                        </div>
                        
                        {/* Score bar */}
                        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden mb-3">
                          <div 
                            className={`h-full transition-all duration-700 ${
                              isRecommended ? 'bg-gradient-to-r from-[#006AFF] to-[#0055CC]' : 
                              isNotAdvised ? 'bg-gray-400' : 'bg-gray-500'
                            }`}
                            style={{ width: `${pathway.score}%` }}
                          />
                        </div>
                        
                        <p className="text-sm text-[#6B6B76] mb-3">{details.description}</p>
                        
                        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                          <span className="text-sm text-[#6B6B76]">{details.rateLabel}:</span>
                          <span className={`text-lg font-bold ${isRecommended ? 'text-[#006AFF]' : 'text-[#2A2A33]'}`}>
                            {details.rateCalc(propertyData)}
                          </span>
                        </div>
                        
                        {/* Pros/Cons for recommended */}
                        {isRecommended && (
                          <div className="mt-4 pt-4 border-t border-[#006AFF]/20">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="text-xs font-semibold text-green-600 mb-2">ADVANTAGES</div>
                                {details.pros.slice(0, 3).map((pro, i) => (
                                  <div key={i} className="flex items-start gap-1 text-xs text-[#6B6B76] mb-1">
                                    <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{pro}</span>
                                  </div>
                                ))}
                              </div>
                              <div>
                                <div className="text-xs font-semibold text-red-600 mb-2">CONSIDERATIONS</div>
                                {details.cons.slice(0, 3).map((con, i) => (
                                  <div key={i} className="flex items-start gap-1 text-xs text-[#6B6B76] mb-1">
                                    <XCircle className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
                                    <span>{con}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* AI Analyst Narrative */}
            <div className="bg-gradient-to-br from-[#F7F7F7] to-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="h-5 w-5 text-[#006AFF]" />
                <h4 className="text-sm font-bold text-[#2A2A33]">AI Analyst Narrative</h4>
                <span className="text-xs text-[#9B9BA5] ml-auto">Credit Committee Memo</span>
              </div>
              
              {narrativeLoading ? (
                <div className="flex items-center gap-3 py-6">
                  <Activity className="h-5 w-5 text-[#006AFF] animate-spin" />
                  <span className="text-sm text-[#6B6B76]">Generating credit committee analysis...</span>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                  <p className="text-sm text-[#2A2A33] leading-relaxed">
                    {narrative}
                  </p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex gap-3 pt-3">
              <Button 
                variant="outline"
                className="flex-1 border-[#006AFF] text-[#006AFF] hover:bg-[#006AFF] hover:text-white py-5"
                onClick={exportMemo}
                data-testid="export-txt-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export TXT
              </Button>
              <Button 
                onClick={generatePDF}
                disabled={exportingPdf || isLoading}
                className="flex-1 bg-gradient-to-r from-[#006AFF] to-[#0055CC] hover:from-[#0055CC] hover:to-[#004499] text-white py-5"
                data-testid="export-pdf-btn"
              >
                {exportingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Export PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DeRiskAnalysisPanel;
